import { useState, useEffect } from 'react';
import { Plus, GraduationCap, AlertTriangle, CheckCircle, Clock, User, Award, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../core/apiClient';
import { formatDate } from '../../lib/utils';

interface Training {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: string;
  duration: number;
  isRequired: boolean;
  validityMonths?: number;
}

interface EmployeeTraining {
  id: string;
  employeeId: string;
  trainingId: string;
  training?: Training;
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
    code: string;
  };
  completedAt: string;
  expiresAt?: string;
  certificateNumber?: string;
  certificateUrl?: string;
  status: string;
  notes?: string;
}

const TRAINING_CATEGORIES = [
  { value: 'PRL', label: 'Prevenció Riscos Laborals' },
  { value: 'TECNIC', label: 'Formació Tècnica' },
  { value: 'SEGURETAT', label: 'Seguretat' },
  { value: 'QUALITAT', label: 'Qualitat' },
  { value: 'IDIOMES', label: 'Idiomes' },
  { value: 'ALTRES', label: 'Altres' },
];

export default function Trainings() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [employeeTrainings, setEmployeeTrainings] = useState<EmployeeTraining[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('catalog');
  
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState<Training | null>(null);

  const [trainingForm, setTrainingForm] = useState({
    code: '',
    name: '',
    description: '',
    category: 'TECNIC',
    duration: '',
    isRequired: false,
    validityMonths: '',
  });

  const [assignForm, setAssignForm] = useState({
    employeeId: '',
    trainingId: '',
    completedAt: new Date().toISOString().split('T')[0],
    certificateNumber: '',
    notes: '',
  });

  const [stats, setStats] = useState({
    totalTrainings: 0,
    employeesWithTrainings: 0,
    expiringSoon: 0,
    expired: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [trainingsRes, empTrainingsRes, employeesRes] = await Promise.all([
        api.get<any>('/trainings'),
        api.get<any>('/trainings/employee-trainings'),
        api.get<any>('/employees'),
      ]);

      const trainingsData = trainingsRes.data?.data || [];
      const empTrainingsData = empTrainingsRes.data?.data || [];
      const employeesData = employeesRes.data?.data || [];

      setTrainings(trainingsData);
      setEmployeeTrainings(empTrainingsData);
      setEmployees(employeesData);

      // Calculate stats
      const now = new Date();
      const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const expiringSoon = empTrainingsData.filter((et: EmployeeTraining) => {
        if (!et.expiresAt) return false;
        const expDate = new Date(et.expiresAt);
        return expDate > now && expDate <= in30Days;
      }).length;

      const expired = empTrainingsData.filter((et: EmployeeTraining) => {
        if (!et.expiresAt) return false;
        return new Date(et.expiresAt) < now;
      }).length;

      const uniqueEmployees = new Set(empTrainingsData.map((et: EmployeeTraining) => et.employeeId)).size;

      setStats({
        totalTrainings: trainingsData.length,
        employeesWithTrainings: uniqueEmployees,
        expiringSoon,
        expired,
      });
    } catch (error) {
      console.error('[v0] Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTraining = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...trainingForm,
        duration: parseInt(trainingForm.duration) || 0,
        validityMonths: trainingForm.validityMonths ? parseInt(trainingForm.validityMonths) : undefined,
      };

      if (editingTraining) {
        await api.put(`/trainings/${editingTraining.id}`, payload);
      } else {
        await api.post('/trainings', payload);
      }

      setShowTrainingModal(false);
      setEditingTraining(null);
      resetTrainingForm();
      loadData();
    } catch (error) {
      console.error('[v0] Error saving training:', error);
    }
  };

  const handleAssignTraining = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/trainings/assign', assignForm);
      setShowAssignModal(false);
      resetAssignForm();
      loadData();
    } catch (error) {
      console.error('[v0] Error assigning training:', error);
    }
  };

  const handleDeleteTraining = async (id: string) => {
    if (!confirm('Segur que vols eliminar aquesta formació?')) return;
    try {
      await api.delete(`/trainings/${id}`);
      loadData();
    } catch (error) {
      console.error('[v0] Error deleting training:', error);
    }
  };

  const resetTrainingForm = () => {
    setTrainingForm({
      code: '',
      name: '',
      description: '',
      category: 'TECNIC',
      duration: '',
      isRequired: false,
      validityMonths: '',
    });
  };

  const resetAssignForm = () => {
    setAssignForm({
      employeeId: '',
      trainingId: '',
      completedAt: new Date().toISOString().split('T')[0],
      certificateNumber: '',
      notes: '',
    });
  };

  const getCategoryLabel = (cat: string) => {
    return TRAINING_CATEGORIES.find(c => c.value === cat)?.label || cat;
  };

  const getStatusBadge = (et: EmployeeTraining) => {
    if (!et.expiresAt) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Vigent</Badge>;
    }
    
    const now = new Date();
    const expDate = new Date(et.expiresAt);
    const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    if (expDate < now) {
      return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3 mr-1" />Caducat</Badge>;
    }
    if (expDate <= in30Days) {
      return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />Pròxim a caducar</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Vigent</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Formació i Certificats</h1>
            <p className="text-slate-500">Gestió de formacions i certificats dels empleats</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { resetAssignForm(); setShowAssignModal(true); }}>
            <Award className="w-4 h-4 mr-2" />
            Assignar Formació
          </Button>
          <Button onClick={() => { resetTrainingForm(); setEditingTraining(null); setShowTrainingModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Formació
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Formacions</p>
                <p className="text-2xl font-bold">{stats.totalTrainings}</p>
              </div>
              <GraduationCap className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Empleats Formats</p>
                <p className="text-2xl font-bold">{stats.employeesWithTrainings}</p>
              </div>
              <User className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pròxims a Caducar</p>
                <p className="text-2xl font-bold text-amber-600">{stats.expiringSoon}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Caducats</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="catalog">
            <GraduationCap className="w-4 h-4 mr-2" />
            Catàleg Formacions
          </TabsTrigger>
          <TabsTrigger value="employees">
            <Award className="w-4 h-4 mr-2" />
            Formacions Empleats
          </TabsTrigger>
          <TabsTrigger value="expiring">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alertes Caducitat
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-4">
          <Card className="rounded-xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codi</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-center">Duració (h)</TableHead>
                    <TableHead className="text-center">Validesa</TableHead>
                    <TableHead className="text-center">Obligatòria</TableHead>
                    <TableHead className="text-right">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">Carregant...</TableCell>
                    </TableRow>
                  ) : trainings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No hi ha formacions al catàleg
                      </TableCell>
                    </TableRow>
                  ) : (
                    trainings.map(training => (
                      <TableRow key={training.id}>
                        <TableCell className="font-mono">{training.code}</TableCell>
                        <TableCell className="font-medium">{training.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCategoryLabel(training.category)}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{training.duration}h</TableCell>
                        <TableCell className="text-center">
                          {training.validityMonths ? `${training.validityMonths} mesos` : 'Permanent'}
                        </TableCell>
                        <TableCell className="text-center">
                          {training.isRequired ? (
                            <Badge className="bg-red-100 text-red-800">Sí</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditingTraining(training);
                            setTrainingForm({
                              code: training.code,
                              name: training.name,
                              description: training.description || '',
                              category: training.category,
                              duration: String(training.duration),
                              isRequired: training.isRequired,
                              validityMonths: training.validityMonths ? String(training.validityMonths) : '',
                            });
                            setShowTrainingModal(true);
                          }}>
                            Editar
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteTraining(training.id)}>
                            Eliminar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <Card className="rounded-xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleat</TableHead>
                    <TableHead>Formació</TableHead>
                    <TableHead>Data Realització</TableHead>
                    <TableHead>Data Caducitat</TableHead>
                    <TableHead>Certificat</TableHead>
                    <TableHead>Estat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">Carregant...</TableCell>
                    </TableRow>
                  ) : employeeTrainings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hi ha formacions assignades
                      </TableCell>
                    </TableRow>
                  ) : (
                    employeeTrainings.map(et => (
                      <TableRow key={et.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            {et.employee?.firstName} {et.employee?.lastName}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{et.training?.name}</TableCell>
                        <TableCell>{formatDate(et.completedAt)}</TableCell>
                        <TableCell>{et.expiresAt ? formatDate(et.expiresAt) : '-'}</TableCell>
                        <TableCell>
                          {et.certificateNumber ? (
                            <span className="font-mono text-sm">{et.certificateNumber}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(et)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expiring" className="mt-4">
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Certificats que Caduquen Pròximament
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleat</TableHead>
                    <TableHead>Formació</TableHead>
                    <TableHead>Data Caducitat</TableHead>
                    <TableHead>Dies Restants</TableHead>
                    <TableHead>Estat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employeeTrainings
                    .filter(et => {
                      if (!et.expiresAt) return false;
                      const now = new Date();
                      const expDate = new Date(et.expiresAt);
                      const in60Days = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
                      return expDate <= in60Days;
                    })
                    .sort((a, b) => new Date(a.expiresAt!).getTime() - new Date(b.expiresAt!).getTime())
                    .map(et => {
                      const now = new Date();
                      const expDate = new Date(et.expiresAt!);
                      const daysLeft = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                      return (
                        <TableRow key={et.id}>
                          <TableCell>
                            {et.employee?.firstName} {et.employee?.lastName}
                          </TableCell>
                          <TableCell className="font-medium">{et.training?.name}</TableCell>
                          <TableCell>{formatDate(et.expiresAt!)}</TableCell>
                          <TableCell>
                            <span className={daysLeft <= 0 ? 'text-red-600 font-bold' : daysLeft <= 30 ? 'text-amber-600 font-medium' : ''}>
                              {daysLeft <= 0 ? 'Caducat' : `${daysLeft} dies`}
                            </span>
                          </TableCell>
                          <TableCell>{getStatusBadge(et)}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Nova Formació - Disabled */}
    </div>
  );
}
